import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';

export default function LoginPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';

  const [tab, setTab] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('tab') === 'register' ? 'register' : 'login';
    } catch { return 'login'; }
  });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  // Login state
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  // Register state
  const [regData, setRegData] = useState({
    email: '', password: '', nickname: '',
  });

  // Reset password — request-only (email); actual reset happens on /reset-password with token
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await login(loginData.email, loginData.password);
      toast.success('Velkommen tilbage!');
      if (!res.has_active_subscription && res.user.email !== 'dannie@yokano.org') {
        navigate('/payment');
      } else {
        navigate(from);
      }
    } catch (err) {
      // Surface timeout/network errors with the friendly message from api.js
      // so the user sees "Serveren svarer ikke" instead of a silent hang.
      const msg = err.friendlyMessage
        || err.response?.data?.detail
        || (err.message ? `Login mislykkedes: ${err.message}` : 'Login mislykkedes');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!(regData.nickname || '').trim()) {
      toast.error('Kaldenavn er påkrævet');
      return;
    }
    setLoading(true);
    try {
      await register(regData);
      toast.success('Konto oprettet! Færdiggør din betaling for at fortsætte.');
      navigate('/payment');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Registrering mislykkedes');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e?.preventDefault?.();
    if (!resetEmail) {
      toast.error('Indtast din email');
      return;
    }
    setResetLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: resetEmail });
      setResetSent(true);
    } catch (err) {
      // Even on error, keep the generic message to avoid email enumeration
      setResetSent(true);
    } finally {
      setResetLoading(false);
    }
  };

  const closeResetDialog = () => {
    setResetOpen(false);
    // Delay resetting inner state so the dialog closes cleanly
    setTimeout(() => {
      setResetSent(false);
      setResetEmail('');
    }, 200);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left */}
      <div className="flex items-center justify-center p-8 lg:p-16 relative z-10">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-4 mb-12 animate-fade-up">
            <img src="/samtale247-logo.webp" alt="samtale24-7.dk" className="w-20 h-20 rounded-full shrink-0" data-testid="login-logo" />
            <div className="font-display text-3xl font-bold tracking-tight">samtale24-7.dk</div>
          </div>

          <div className="mb-8 animate-fade-up" style={{ animationDelay: '80ms' }}>
            <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight leading-[1.1] whitespace-nowrap">
              <span className="text-volt">Deltag med/uden video</span>
            </h1>
          </div>

          <Tabs value={tab} onValueChange={setTab} className="animate-fade-up" style={{ animationDelay: '160ms' }}>
            <TabsList className="grid w-full grid-cols-2 bg-[#18181B] border border-border">
              <TabsTrigger value="login" data-testid="tab-login">Log ind</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Opret konto</TabsTrigger>
            </TabsList>

            <TabsContent value="login" className="mt-6">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="l-email">Email</Label>
                  <Input id="l-email" type="email" value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="bg-[#18181B] border-border mt-1.5 focus-visible:ring-volt"
                    required data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="l-pwd">Adgangskode</Label>
                    <button type="button" onClick={() => setResetOpen(true)}
                      className="text-xs text-muted-foreground hover:text-volt transition-colors"
                      data-testid="forgot-password-link">
                      Glemt adgangskode?
                    </button>
                  </div>
                  <div className="relative mt-1.5">
                    <Input id="l-pwd" type={showPwd ? 'text' : 'password'} value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      className="bg-[#18181B] border-border pr-10 focus-visible:ring-volt"
                      required data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPwd(!showPwd)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-white">
                      {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
                  data-testid="login-submit-btn">
                  {/* Stable DOM structure across loading states: same <span> in position 0,
                      optional icon in position 1. Avoids React "removeChild" crash when
                      a browser translator (Google/iOS Safari Translate) wraps text nodes. */}
                  <span translate="no">{loading ? 'Logger ind…' : 'Log ind'}</span>
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </TabsContent>

            <TabsContent value="register" className="mt-6">
              <form onSubmit={handleRegister} className="space-y-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={regData.email}
                    onChange={(e) => setRegData({ ...regData, email: e.target.value })}
                    className="bg-[#18181B] border-border mt-1.5" required
                    data-testid="register-email-input" />
                </div>
                <div>
                  <Label>Kaldenavn <span className="text-volt">*</span></Label>
                  <Input value={regData.nickname}
                    onChange={(e) => setRegData({ ...regData, nickname: e.target.value })}
                    className="bg-[#18181B] border-border mt-1.5" required
                    placeholder="fx Anna_K"
                    data-testid="register-nickname-input" />
                  <p className="text-xs text-muted-foreground mt-1">Vises i videomøder. Kan ikke ændres efter oprettelse.</p>
                </div>
                <div>
                  <Label>Adgangskode</Label>
                  <Input type="password" value={regData.password}
                    onChange={(e) => setRegData({ ...regData, password: e.target.value })}
                    className="bg-[#18181B] border-border mt-1.5" required minLength={6}
                    data-testid="register-password-input" />
                </div>
                <Button type="submit" disabled={loading}
                  className="w-full bg-volt text-black hover:bg-volt hover:-translate-y-[1px] hover:shadow-lg transition-all font-semibold"
                  data-testid="register-submit-btn">
                  <span translate="no">{loading ? 'Opretter…' : 'Opret konto'}</span>
                  {!loading && <ArrowRight className="w-4 h-4 ml-2" />}
                </Button>
              </form>
            </TabsContent>
          </Tabs>

          <div className="mt-8 text-xs text-muted-foreground animate-fade-up" style={{ animationDelay: '240ms' }}>
            Ved at fortsætte accepterer du vores vilkår.
          </div>
        </div>
      </div>

      {/* Right - visual */}
      <div className="hidden lg:block relative overflow-hidden bg-[#0d0d10]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/33797643/pexels-photo-33797643.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#09090B] via-transparent to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <div className="backdrop-blur-2xl bg-black/60 border border-white/10 rounded-lg p-6 max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-volt pulse-dot" />
              <span className="text-xs uppercase tracking-widest text-volt font-semibold">Der er åbne grupper lige nu</span>
            </div>
            <div className="font-display text-2xl font-bold mb-2">Vælg selv hvilken gruppe du vil deltage i</div>
          </div>
        </div>
      </div>

      {/* Reset dialog */}
      <Dialog open={resetOpen} onOpenChange={(v) => (v ? setResetOpen(true) : closeResetDialog())}>
        <DialogContent className="bg-[#18181B] border-border">
          <DialogHeader>
            <DialogTitle className="font-display">Nulstil adgangskode</DialogTitle>
          </DialogHeader>
          {!resetSent ? (
            <form onSubmit={handleReset} className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Indtast din email. Hvis den findes i vores system, sender vi et link til at nulstille din adgangskode. Linket er gyldigt i 1 time.
              </p>
              <div>
                <Label>Email</Label>
                <Input type="email" value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  className="bg-[#09090B] border-border mt-1.5"
                  required
                  data-testid="reset-email-input" />
              </div>
              <DialogFooter>
                <Button type="submit" disabled={resetLoading}
                  className="bg-volt text-black hover:bg-volt font-semibold"
                  data-testid="reset-submit-btn">
                  {resetLoading ? 'Sender…' : 'Send nulstillingslink'}
                </Button>
              </DialogFooter>
            </form>
          ) : (
            <div className="space-y-4" data-testid="reset-sent-message">
              <p className="text-sm text-white">
                Hvis <span className="text-volt font-semibold">{resetEmail}</span> findes i vores system, har vi sendt et link til nulstilling af adgangskoden.
              </p>
              <p className="text-xs text-muted-foreground">
                Tjek din indbakke (og spam-mappen). Linket udløber om 1 time og kan kun bruges én gang.
              </p>
              <DialogFooter>
                <Button onClick={closeResetDialog}
                  className="bg-volt text-black hover:bg-volt font-semibold"
                  data-testid="reset-close-btn">
                  OK
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
